"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.genTransferSporeCkbVirtualTx = exports.appendIssuerCellToSporesCreate = exports.buildAppendingIssuerCellToSporesCreateTx = exports.genCreateSporeCkbVirtualTx = void 0;
const core_1 = require("@spore-sdk/core");
const utils_1 = require("../utils");
const rgbpp_1 = require("../utils/rgbpp");
const constants_1 = require("../constants");
const spore_1 = require("../utils/spore");
const error_1 = require("../error");
const ckb_sdk_utils_1 = require("@nervosnetwork/ckb-sdk-utils");
const signWitnesses_1 = __importDefault(require("@nervosnetwork/ckb-sdk-core/lib/signWitnesses"));
/**
 * Generate the virtual ckb transaction for creating spores
 * @param collector The collector that collects CKB live cells and transactions
 * @param clusterRgbppLockArgs The cluster rgbpp cell lock script args whose data structure is: out_index | bitcoin_tx_id
 * @param sporeDataList The spore's data list, including name and description.
 * @param isMainnet True is for BTC and CKB Mainnet, false is for BTC and CKB Testnet(see btcTestnetType for details about BTC Testnet)
 * @param btcTestnetType(Optional) The Bitcoin Testnet type including Testnet3 and Signet, default value is Testnet3
 */
const genCreateSporeCkbVirtualTx = (_a) => __awaiter(void 0, [_a], void 0, function* ({ collector, clusterRgbppLockArgs, sporeDataList, isMainnet, btcTestnetType, }) {
    const clusterRgbppLock = (0, rgbpp_1.genRgbppLockScript)(clusterRgbppLockArgs, isMainnet, btcTestnetType);
    const clusterCells = yield collector.getCells({ lock: clusterRgbppLock, isDataMustBeEmpty: false });
    if (!clusterCells || clusterCells.length === 0) {
        throw new error_1.NoRgbppLiveCellError('No cluster rgbpp cells found with the cluster rgbpp lock args');
    }
    if (clusterCells.length > 1) {
        throw new error_1.RgbppUtxoBindMultiTypeAssetsError('The BTC UTXO must not be bound to multiple CKB cells');
    }
    const clusterCell = clusterCells[0];
    if (!clusterCell.output.type || !(0, utils_1.isClusterSporeTypeSupported)(clusterCell.output.type, isMainnet)) {
        throw new error_1.TypeAssetNotSupportedError('The type script asset is not supported now');
    }
    const sumInputsCapacity = clusterCell.output.capacity;
    const inputs = [
        {
            previousOutput: clusterCell.outPoint,
            since: '0x0',
        },
    ];
    const clusterCellDep = {
        outPoint: clusterCell.outPoint,
        depType: 'code',
    };
    const sporeOutputs = sporeDataList.map((data, index) => ({
        // The BTC transaction Vouts[0] for OP_RETURN, Vouts[1] for cluster and Vouts[2]... for spore
        lock: (0, rgbpp_1.genRgbppLockScript)((0, rgbpp_1.buildPreLockArgs)(index + 2), isMainnet, btcTestnetType),
        type: Object.assign(Object.assign({}, (0, constants_1.getSporeTypeScript)(isMainnet)), { 
            // The CKB transaction outputs[0] fro cluster and outputs[1]... for spore
            args: (0, spore_1.generateSporeId)(inputs[0], index + 1) }),
        capacity: (0, utils_1.append0x)((0, utils_1.calculateRgbppSporeCellCapacity)(data).toString(16)),
    }));
    const sporeOutputsData = sporeDataList.map((data) => (0, ckb_sdk_utils_1.bytesToHex)((0, core_1.packRawSporeData)(data)));
    const outputs = [
        Object.assign(Object.assign({}, clusterCell.output), { 
            // The BTC transaction Vouts[0] for OP_RETURN, Vouts[1] for cluster
            lock: (0, rgbpp_1.genRgbppLockScript)((0, rgbpp_1.buildPreLockArgs)(1), isMainnet, btcTestnetType) }),
        ...sporeOutputs,
    ];
    const outputsData = [clusterCell.outputData, ...sporeOutputsData];
    const cellDeps = [
        ...(yield (0, utils_1.fetchTypeIdCellDeps)(isMainnet, { rgbpp: true }, btcTestnetType)),
        (0, constants_1.getClusterTypeDep)(isMainnet),
        (0, constants_1.getSporeTypeDep)(isMainnet),
        clusterCellDep,
    ];
    const sporeCoBuild = (0, spore_1.generateSporeCreateCoBuild)({
        sporeOutputs,
        sporeOutputsData,
        clusterCell,
        clusterOutputCell: outputs[0],
    });
    const witnesses = [constants_1.RGBPP_WITNESS_PLACEHOLDER, sporeCoBuild];
    const ckbRawTx = {
        version: '0x0',
        cellDeps,
        headerDeps: [],
        inputs,
        outputs,
        outputsData,
        witnesses,
    };
    const virtualTx = Object.assign({}, ckbRawTx);
    const commitment = (0, rgbpp_1.calculateCommitment)(virtualTx);
    return {
        ckbRawTx,
        commitment,
        sumInputsCapacity,
        clusterCell,
        needPaymasterCell: false,
    };
});
exports.genCreateSporeCkbVirtualTx = genCreateSporeCkbVirtualTx;
const CELL_DEP_SIZE = 32 + 4 + 1;
/**
 * Append paymaster cell to the ckb transaction inputs and build the raw tx to be signed for spores creation
 * @param issuerAddress The issuer ckb address
 * @param collector The collector that collects CKB live cells and transactions
 * @param ckbRawTx CKB raw transaction
 * @param sumInputsCapacity The sum capacity of ckb inputs which is to be used to calculate ckb tx fee
 * @param witnessLockPlaceholderSize(Optional) The WitnessArgs.lock placeholder bytes array size and the default value is 65
 * @param ckbFeeRate(Optional) The CKB transaction fee rate, default value is 1100
 */
const buildAppendingIssuerCellToSporesCreateTx = (_a) => __awaiter(void 0, [_a], void 0, function* ({ issuerAddress, collector, ckbRawTx, sumInputsCapacity, witnessLockPlaceholderSize = constants_1.SECP256K1_WITNESS_LOCK_SIZE, ckbFeeRate, }) {
    const rawTx = ckbRawTx;
    const sumOutputsCapacity = rawTx.outputs
        .map((output) => BigInt(output.capacity))
        .reduce((prev, current) => prev + current, BigInt(0));
    const issuerLock = (0, ckb_sdk_utils_1.addressToScript)(issuerAddress);
    let emptyCells = yield collector.getCells({ lock: issuerLock });
    if (!emptyCells || emptyCells.length === 0) {
        throw new error_1.NoLiveCellError('The issuer address has no empty cells');
    }
    emptyCells = emptyCells.filter((cell) => !cell.output.type);
    let actualInputsCapacity = BigInt(sumInputsCapacity);
    const txFee = constants_1.MAX_FEE;
    if (actualInputsCapacity <= sumOutputsCapacity) {
        const needCapacity = sumOutputsCapacity - actualInputsCapacity + constants_1.MIN_CAPACITY;
        const { inputs, sumInputsCapacity: sumEmptyCapacity } = collector.collectInputs(emptyCells, needCapacity, txFee);
        rawTx.inputs = [...rawTx.inputs, ...inputs];
        actualInputsCapacity += sumEmptyCapacity;
    }
    let changeCapacity = actualInputsCapacity - sumOutputsCapacity;
    const changeOutput = {
        lock: issuerLock,
        capacity: (0, utils_1.append0x)(changeCapacity.toString(16)),
    };
    rawTx.outputs = [...rawTx.outputs, changeOutput];
    rawTx.outputsData = [...rawTx.outputsData, '0x'];
    const txSize = (0, ckb_sdk_utils_1.getTransactionSize)(rawTx) + witnessLockPlaceholderSize + CELL_DEP_SIZE;
    const estimatedTxFee = (0, utils_1.calculateTransactionFee)(txSize, ckbFeeRate);
    changeCapacity -= estimatedTxFee;
    rawTx.outputs[rawTx.outputs.length - 1].capacity = (0, utils_1.append0x)(changeCapacity.toString(16));
    return rawTx;
});
exports.buildAppendingIssuerCellToSporesCreateTx = buildAppendingIssuerCellToSporesCreateTx;
/**
 * Append paymaster cell to the ckb transaction inputs and sign the transaction with paymaster cell's secp256k1 private key
 * @param secp256k1PrivateKey The Secp256k1 private key of the paymaster cells maintainer
 * @param issuerAddress The issuer ckb address
 * @param collector The collector that collects CKB live cells and transactions
 * @param ckbRawTx CKB raw transaction
 * @param sumInputsCapacity The sum capacity of ckb inputs which is to be used to calculate ckb tx fee
 * @param isMainnet True is for BTC and CKB Mainnet, false is for BTC and CKB Testnet(see btcTestnetType for details about BTC Testnet)
 * @param ckbFeeRate(Optional) The CKB transaction fee rate, default value is 1100
 */
const appendIssuerCellToSporesCreate = (_a) => __awaiter(void 0, [_a], void 0, function* ({ secp256k1PrivateKey, issuerAddress, collector, ckbRawTx, sumInputsCapacity, isMainnet, ckbFeeRate, }) {
    const rgbppInputsLength = ckbRawTx.inputs.length;
    const rawTx = yield (0, exports.buildAppendingIssuerCellToSporesCreateTx)({
        issuerAddress,
        collector,
        ckbRawTx,
        sumInputsCapacity,
        ckbFeeRate,
    });
    rawTx.cellDeps = [...rawTx.cellDeps, (0, constants_1.getSecp256k1CellDep)(isMainnet)];
    const issuerLock = (0, ckb_sdk_utils_1.addressToScript)(issuerAddress);
    const keyMap = new Map();
    keyMap.set((0, ckb_sdk_utils_1.scriptToHash)(issuerLock), secp256k1PrivateKey);
    const issuerCellIndex = rgbppInputsLength;
    const cells = rawTx.inputs.map((input, index) => ({
        outPoint: input.previousOutput,
        lock: index >= issuerCellIndex ? issuerLock : (0, constants_1.getRgbppLockScript)(isMainnet),
    }));
    const emptyWitness = { lock: '', inputType: '', outputType: '' };
    const issuerWitnesses = rawTx.inputs.slice(rgbppInputsLength).map((_, index) => (index === 0 ? emptyWitness : '0x'));
    const lastRawTxWitnessIndex = rawTx.witnesses.length - 1;
    rawTx.witnesses = [
        ...rawTx.witnesses.slice(0, lastRawTxWitnessIndex),
        ...issuerWitnesses,
        // The cobuild witness will be placed to the tail of the witnesses
        rawTx.witnesses[lastRawTxWitnessIndex],
    ];
    const transactionHash = (0, ckb_sdk_utils_1.rawTransactionToHash)(rawTx);
    const signedWitnesses = (0, signWitnesses_1.default)(keyMap)({
        transactionHash,
        witnesses: rawTx.witnesses,
        inputCells: cells,
        skipMissingKeys: true,
    });
    const signedTx = Object.assign(Object.assign({}, rawTx), { witnesses: signedWitnesses.map((witness) => typeof witness !== 'string' ? (0, ckb_sdk_utils_1.serializeWitnessArgs)(witness) : witness) });
    return signedTx;
});
exports.appendIssuerCellToSporesCreate = appendIssuerCellToSporesCreate;
/**
 * Generate the virtual ckb transaction for transferring spore
 * @param collector The collector that collects CKB live cells and transactions
 * @param sporeRgbppLockArgs The spore rgbpp cell lock script args whose data structure is: out_index | bitcoin_tx_id
 * @param sporeTypeBytes The spore type script serialized bytes
 * @param isMainnet True is for BTC and CKB Mainnet, false is for BTC and CKB Testnet(see btcTestnetType for details about BTC Testnet)
 * @param witnessLockPlaceholderSize(Optional) The WitnessArgs.lock placeholder bytes array size and the default value is 5000
 * @param ckbFeeRate(Optional) The CKB transaction fee rate, default value is 1100
 * @param btcTestnetType(Optional) The Bitcoin Testnet type including Testnet3 and Signet, default value is Testnet3
 */
const genTransferSporeCkbVirtualTx = (_a) => __awaiter(void 0, [_a], void 0, function* ({ collector, sporeRgbppLockArgs, sporeTypeBytes, isMainnet, witnessLockPlaceholderSize, ckbFeeRate, btcTestnetType, }) {
    const sporeRgbppLock = (0, rgbpp_1.genRgbppLockScript)(sporeRgbppLockArgs, isMainnet, btcTestnetType);
    const sporeCells = yield collector.getCells({ lock: sporeRgbppLock, isDataMustBeEmpty: false });
    (0, spore_1.throwErrorWhenSporeCellsInvalid)(sporeCells, sporeTypeBytes, isMainnet);
    const sporeCell = sporeCells[0];
    const inputs = [
        {
            previousOutput: sporeCell.outPoint,
            since: '0x0',
        },
    ];
    const outputs = [
        Object.assign(Object.assign({}, sporeCell.output), { 
            // The BTC transaction Vouts[0] for OP_RETURN, Vouts[1] for spore
            lock: (0, rgbpp_1.genRgbppLockScript)((0, rgbpp_1.buildPreLockArgs)(1), isMainnet, btcTestnetType) }),
    ];
    const outputsData = [sporeCell.outputData];
    const cellDeps = [
        ...(yield (0, utils_1.fetchTypeIdCellDeps)(isMainnet, { rgbpp: true }, btcTestnetType)),
        (0, constants_1.getSporeTypeDep)(isMainnet),
    ];
    const sporeCoBuild = (0, spore_1.generateSporeTransferCoBuild)([sporeCell], outputs);
    const witnesses = [constants_1.RGBPP_WITNESS_PLACEHOLDER, sporeCoBuild];
    const ckbRawTx = {
        version: '0x0',
        cellDeps,
        headerDeps: [],
        inputs,
        outputs,
        outputsData,
        witnesses,
    };
    let changeCapacity = BigInt(sporeCell.output.capacity);
    const txSize = (0, ckb_sdk_utils_1.getTransactionSize)(ckbRawTx) + (witnessLockPlaceholderSize !== null && witnessLockPlaceholderSize !== void 0 ? witnessLockPlaceholderSize : constants_1.RGBPP_TX_WITNESS_MAX_SIZE);
    const estimatedTxFee = (0, utils_1.calculateTransactionFee)(txSize, ckbFeeRate);
    changeCapacity -= estimatedTxFee;
    ckbRawTx.outputs[ckbRawTx.outputs.length - 1].capacity = (0, utils_1.append0x)(changeCapacity.toString(16));
    const virtualTx = Object.assign({}, ckbRawTx);
    const commitment = (0, rgbpp_1.calculateCommitment)(virtualTx);
    return {
        ckbRawTx,
        commitment,
        sporeCell,
        needPaymasterCell: false,
        sumInputsCapacity: sporeCell.output.capacity,
    };
});
exports.genTransferSporeCkbVirtualTx = genTransferSporeCkbVirtualTx;
